import { DownOutlined, UpOutlined } from '@ant-design/icons'
import { Button, Checkbox, Col, Row } from 'antd'
import { useState } from 'react'
import type { CheckpointDependency } from '@/utils/checkpointDependency'
import {
  getMappedValues,
  includesIgnoreCase,
} from '@/utils/checkpointDependency'

type Props = {
  dependency: CheckpointDependency
  triggerDataSource: any[]
  dataSource: any[]
  onToggleMappedOption: (
    triggerValue: string,
    optionValue: string,
    checked: boolean
  ) => void
  onSelectAllMappedOptions: (triggerValue: string) => void
}

function CheckpointMapOptions({
  dependency,
  triggerDataSource,
  dataSource,
  onToggleMappedOption,
  onSelectAllMappedOptions,
}: Props) {
  const [expanded, setExpanded] = useState<string[]>([])
  const dataSourceCount = dataSource?.length || 0

  const toggleExpand = (triggerValue: string) => {
    setExpanded((prev) =>
      prev.includes(triggerValue)
        ? prev.filter((v) => v !== triggerValue)
        : [...prev, triggerValue]
    )
  }

  return (
    <Row align="top" className="checkpoint-option-container map-options-row">
      <Col span={6}>
        <p className="label">Map options</p>
      </Col>
      <Col span={16} className="map-options-list">
        {(triggerDataSource || []).map((triggerOpt) => {
          const triggerValue = triggerOpt.value
          const mapped = getMappedValues(dependency?.mappings, triggerValue)
          const mappedCount = mapped.length
          const isEmptyMap = mappedCount === 0
          const isExpanded = expanded.includes(triggerValue)

          return (
            <div key={triggerValue} className="map-option-row">
              <Row
                align="middle"
                justify="space-between"
                wrap={false}
                className={`map-option-header ${
                  isEmptyMap ? 'map-option-error' : ''
                }`}
                role="button"
                tabIndex={0}
                onClick={() => toggleExpand(triggerValue)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    toggleExpand(triggerValue)
                  }
                }}
              >
                <Col flex="auto" className="map-option-label-col">
                  <span className="map-option-label">
                    {triggerOpt.label || triggerValue}
                  </span>
                </Col>
                <Col flex="none">
                  <span className="map-option-count">
                    {mappedCount} of {dataSourceCount} mapped
                  </span>
                  {isExpanded ? (
                    <UpOutlined className="map-option-chevron" />
                  ) : (
                    <DownOutlined className="map-option-chevron" />
                  )}
                </Col>
              </Row>

              {isExpanded && (
                <div className="map-option-body">
                  <div className="map-option-actions">
                    <Button
                      type="link"
                      size="small"
                      className="map-option-action-link"
                      onClick={(e) => {
                        e.stopPropagation()
                        onSelectAllMappedOptions(triggerValue)
                      }}
                    >
                      Select all
                    </Button>
                  </div>
                  {(dataSource || []).map((opt) => (
                    <div key={opt.value} className="map-option-check">
                      <Checkbox
                        checked={includesIgnoreCase(mapped, opt.value)}
                        onChange={(e) =>
                          onToggleMappedOption(
                            triggerValue,
                            opt.value,
                            e.target.checked
                          )
                        }
                      >
                        {opt.label || opt.value}
                      </Checkbox>
                    </div>
                  ))}
                </div>
              )}

              {isEmptyMap && (
                <p className="map-option-error-text">
                  No options mapped. At least one option is required.
                </p>
              )}
            </div>
          )
        })}
      </Col>
    </Row>
  )
}

export default CheckpointMapOptions
